import Breadcrumbs from '@components/Breadcrumbs'
import PageHeading from '@components/PageHeading'
import { PublicShell } from '@components/PublicPage'
import { usePublicOverview } from '@hooks'
import { publicTripPath } from '@lib/publicLinks'
import React from 'react'
import { Container, CrumbRow, TopBar } from './PublicSectionLayout.styles'

interface PublicSectionLayoutProps {
	/** The shared trip code; the breadcrumb trail and share URLs compose from it. */
	tripCode: string
	/** Section heading, e.g. "Ideas", "Accommodations". */
	title: string
	/** Optional count / context line under the title, e.g. "3 places". */
	subtitle?: string
	/** Intermediate crumb for per-item detail pages (their section list). */
	parentCrumb?: { label: string; path: string }
	children: React.ReactNode
}

/**
 * Happy-path frame for every public per-section detail page: the shared
 * `PublicShell` chrome (SiteHeader view-only pill + Get Orbit CTA, cream
 * surface, footer caption) around a breadcrumb trail — trip name →
 * [section list →] current page, same shape as member mode, rooted at the
 * shared overview — then the section title and content. Deliberately minimal —
 * no app Layout, no `CtaBand` (kept for the overview marketing surface only).
 * Loading / unavailable / error branches are rendered by `PublicSectionGate`,
 * not this layout.
 */
const PublicSectionLayout: React.FC<PublicSectionLayoutProps> = ({
	tripCode,
	title,
	subtitle,
	parentCrumb,
	children,
}) => {
	// Trip name for the trail root — already cached from the overview page the
	// visitor clicked through (and fetched here on a direct link).
	const { data: overview } = usePublicOverview(tripCode)

	const crumbs = [
		{ label: overview?.trip.name ?? 'Trip', path: publicTripPath(tripCode) },
		...(parentCrumb ? [parentCrumb] : []),
		{ label: title },
	]

	return (
		<PublicShell>
			<Container>
				<TopBar>
					<CrumbRow>
						<Breadcrumbs items={crumbs} />
					</CrumbRow>
					<PageHeading title={title} subtitle={subtitle} />
				</TopBar>
				{children}
			</Container>
		</PublicShell>
	)
}

export default PublicSectionLayout
